// src/pages/About/About.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Chip,
  Paper,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  CheckCircle,
  People,
  Devices,
  Code,
  EmojiEvents,
  Visibility,
  TrendingUp,
} from "@mui/icons-material";
import styles from "./About.module.css";

// Animated Counter Component
const AnimatedCounter = ({ target, duration = 2000, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const counterRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 },
    );

    if (counterRef.current) {
      observer.observe(counterRef.current);
    }

    return () => {
      if (counterRef.current) {
        observer.unobserve(counterRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    let startTime = null;
    const startValue = 0;
    const endValue = target;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);

      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentValue = Math.floor(
        easeOutQuart * (endValue - startValue) + startValue,
      );

      setCount(currentValue);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(endValue);
      }
    };

    requestAnimationFrame(animate);
  }, [isVisible, target, duration]);

  return (
    <span ref={counterRef} className={styles.counterNumber}>
      {count}
      {suffix}
    </span>
  );
};

const About = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Stats Data with targets for animation
  const stats = [
    { number: 1250, label: "Staff Members", icon: <People />, suffix: "+" },
    { number: 996, label: "Happy Clients", icon: <EmojiEvents />, suffix: "+" },
    { number: 15, label: "Hardware Products", icon: <Devices />, suffix: "+" },
    { number: 25, label: "Software Solutions", icon: <Code />, suffix: "+" },
  ];

  // Core Values Data
  const coreValues = [
    { title: "Transparency", icon: "🔍", color: "#8cc61d" },
    { title: "Accountability", icon: "📋", color: "#4a90d9" },
    { title: "Integrity", icon: "⚖️", color: "#9b59b6" },
    { title: "Equality", icon: "🤝", color: "#f39c12" },
  ];

  // Work Ethics Data
  const workEthics = [
    {
      title: "Quality",
      description: "Delivering excellence in everything we do",
    },
    { title: "Commitment", description: "Dedicated to our clients' success" },
    {
      title: "Communication",
      description: "Clear, transparent, and consistent",
    },
    {
      title: "Respect for Human Rights",
      description: "Treating everyone with dignity and fairness",
    },
  ];

  return (
    <Box className={styles.aboutPage}>
      {/* ===== HERO SECTION WITH ANIMATED BACKGROUND ===== */}
      <Box className={styles.heroSection}>
        {/* Animated Background Elements */}
        <Box className={styles.animatedBg}>
          {/* Floating Particles */}
          <Box className={`${styles.particle} ${styles.particle1}`}></Box>
          <Box className={`${styles.particle} ${styles.particle2}`}></Box>
          <Box className={`${styles.particle} ${styles.particle3}`}></Box>
          <Box className={`${styles.particle} ${styles.particle4}`}></Box>
          <Box className={`${styles.particle} ${styles.particle5}`}></Box>
          <Box className={`${styles.particle} ${styles.particle6}`}></Box>
          <Box className={`${styles.particle} ${styles.particle7}`}></Box>
          <Box className={`${styles.particle} ${styles.particle8}`}></Box>

          {/* Floating Shapes */}
          <Box className={`${styles.shape} ${styles.shape1}`}>
            <Box className={styles.shapeInner}></Box>
          </Box>
          <Box className={`${styles.shape} ${styles.shape2}`}>
            <Box className={styles.shapeInner}></Box>
          </Box>
          <Box className={`${styles.shape} ${styles.shape3}`}>
            <Box className={styles.shapeInner}></Box>
          </Box>

          {/* Floating Lines */}
          <Box className={`${styles.line} ${styles.line1}`}></Box>
          <Box className={`${styles.line} ${styles.line2}`}></Box>

          {/* Floating Dots Grid */}
          <Box className={styles.dotsGrid}>
            <Box className={styles.dot}></Box>
            <Box className={styles.dot}></Box>
            <Box className={styles.dot}></Box>
            <Box className={styles.dot}></Box>
            <Box className={styles.dot}></Box>
            <Box className={styles.dot}></Box>
            <Box className={styles.dot}></Box>
            <Box className={styles.dot}></Box>
            <Box className={styles.dot}></Box>
          </Box>
        </Box>

        <Container maxWidth="lg">
          <Box className={styles.heroContent}>
            <Typography
              variant="h1"
              className={styles.heroTitle}
              sx={{
                fontSize: { xs: "2.5rem", sm: "3.5rem", md: "4rem" },
              }}
            >
              Who <span className={styles.highlight}>We Are</span>
            </Typography>
            <Typography variant="h6" className={styles.heroSubtitle}>
              UK-based engineering solutions provider and software development
              company
            </Typography>

            {/* Scroll Indicator */}
            <Box className={styles.scrollIndicator}>
              <Box className={styles.scrollMouse}>
                <Box className={styles.scrollWheel}></Box>
              </Box>
              <Typography variant="caption" className={styles.scrollText}>
                Scroll to explore
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* WHO WE ARE Section */}
      <Box className={styles.whoWeAreSection}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Box className={styles.whoWeAreContent}>
                <Typography
                  variant="h2"
                  className={styles.sectionTitle}
                  sx={{
                    fontSize: { xs: "2rem", sm: "2.5rem", md: "2.8rem" },
                  }}
                >
                  Wavelength Consultancy <br />
                  <span className={styles.highlight}>Solutions</span>
                </Typography>
                <Typography variant="body1" className={styles.sectionText}>
                  <strong>Wavelength Consultancy Solutions (WCS)</strong> is a
                  UK based, engineering solutions provider and software
                  development company.
                </Typography>
                <Typography variant="body1" className={styles.sectionText}>
                  We innovatively design and manufacture cutting-edge electronic
                  products and develop various software applications for
                  start-up and blue-chip companies. These include: tablet PCs,
                  mobile phones, wi-fi infrastructure, health monitors and other
                  components.
                </Typography>
                <Typography variant="body1" className={styles.sectionText}>
                  We have successfully developed our own brand of hardware and
                  software which are being utilized by clients from across the{" "}
                  <strong>
                    education, medical, transportation, leisure, media,
                    entertainment and financial sectors
                  </strong>
                  . A key strength is our ability to customize these products
                  and services to meet our customers' needs and demands.
                </Typography>
                <Typography variant="body1" className={styles.sectionText}>
                  We have a global footprint, providing products and services in
                  the global market. We are involved in several projects in the
                  developed world and we are also helping to build technology
                  capability in developing countries with a focus on{" "}
                  <strong>Africa</strong>.
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box className={styles.whoWeWeImage}>
                <Paper elevation={0} className={styles.statsGrid}>
                  <Grid container spacing={2}>
                    {stats.map((stat, index) => (
                      <Grid size={{ xs: 6, sm: 6 }} key={index}>
                        <Box className={styles.statCard}>
                          <Box className={styles.statIcon}>{stat.icon}</Box>
                          <Typography
                            variant="h3"
                            className={styles.statNumber}
                          >
                            <AnimatedCounter
                              target={stat.number}
                              duration={2000}
                              suffix={stat.suffix || ""}
                            />
                          </Typography>
                          <Typography
                            variant="body2"
                            className={styles.statLabel}
                          >
                            {stat.label}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* HOW WE STARTED Section */}
      <Box className={styles.startedSection}>
        <Container maxWidth="lg">
          <Box className={styles.sectionHeader}>
            <Typography
              variant="h2"
              className={styles.sectionTitle}
              sx={{
                fontSize: { xs: "2rem", sm: "2.5rem", md: "2.8rem" },
              }}
            >
              Our <span className={styles.highlight}>Journey</span>
            </Typography>
          </Box>

          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box className={styles.startedContent}>
                <Typography variant="body1" className={styles.sectionText}>
                  <strong>Wavelength Consultancy Solutions (WCS)</strong>, is a
                  UK-headquartered provider of IT consulting services and
                  bespoke software development with over{" "}
                  <strong>50 IT professionals</strong> located internationally.
                  Its subsidiary, <strong>Wavepalm</strong>, focuses on the
                  development of hardware and software solutions.
                </Typography>
                <Typography variant="body1" className={styles.sectionText}>
                  For over <strong>10 years</strong>, we have been manufacturing
                  high tech products, developing world class mobile apps,
                  training young minds to be experts in the field of technology
                  and providing business advisory services.
                </Typography>
                <Typography variant="body1" className={styles.sectionText}>
                  As an Engineering and Software company, we pride ourselves on
                  the high level quality and efficiency of our services and
                  products – proven by our extensive customer base.
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box className={styles.startedContent}>
                <Typography variant="body1" className={styles.sectionText}>
                  Our current and potential clients in the global market are
                  from a range of sectors such as the{" "}
                  <strong>
                    education, medical, entertainment, media, transportation,
                    leisure
                  </strong>{" "}
                  and many more sectors.
                </Typography>
                <Typography variant="body1" className={styles.sectionText}>
                  We have grown as a socially conscious organization with a
                  culture of giving back to our communities by undertaking
                  charity projects in the UK and across Africa.
                </Typography>
                <Box className={styles.visionBox}>
                  <Typography variant="h6" className={styles.visionTitle}>
                    🌍 Wavepalm Africa
                  </Typography>
                  <Typography variant="body2">
                    Provides training services to the youths to develop experts
                    in the field of technology. We also provide business
                    advisory services.
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Vision & Mission Section */}
      <Box className={styles.visionMissionSection}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper elevation={0} className={styles.visionCard}>
                <Box className={styles.visionIcon}>
                  <Visibility sx={{ fontSize: 48 }} />
                </Box>
                <Typography variant="h4" className={styles.visionCardTitle}>
                  Vision Statement
                </Typography>
                <Typography variant="body1" className={styles.visionCardText}>
                  Our main vision is to deliver quality and satisfactory service
                  to our clients maintaining a positive impact in order to
                  influence the world through our attitude to business while
                  making great waves in technology.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper elevation={0} className={styles.missionCard}>
                <Box className={styles.missionIcon}>
                  <TrendingUp sx={{ fontSize: 48 }} />
                </Box>
                <Typography variant="h4" className={styles.missionCardTitle}>
                  Mission Statement
                </Typography>
                <Typography variant="body1" className={styles.missionCardText}>
                  Our mission as a world leading technology solutions provider,
                  passionate about creating solutions to fit the needs of our
                  customers, is to stay ahead of technology while ensuring that
                  we have set objectives in place to guide our operations.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Objectives & Market Leadership */}
      <Box className={styles.objectivesSection}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box className={styles.objectivesBox}>
                <Typography variant="h4" className={styles.objectivesTitle}>
                  🎯 Objectives
                </Typography>
                <Typography variant="body1" className={styles.sectionText}>
                  We aim to deploy all our resources towards achieving{" "}
                  <strong>total customer satisfaction</strong>, especially
                  through the provision of quality products and services.
                </Typography>
                <Box className={styles.objectivesList}>
                  <Box className={styles.objectiveItem}>
                    <CheckCircle className={styles.objectiveCheck} />
                    <Typography variant="body2">
                      Quality Products & Services
                    </Typography>
                  </Box>
                  <Box className={styles.objectiveItem}>
                    <CheckCircle className={styles.objectiveCheck} />
                    <Typography variant="body2">
                      Total Customer Satisfaction
                    </Typography>
                  </Box>
                  <Box className={styles.objectiveItem}>
                    <CheckCircle className={styles.objectiveCheck} />
                    <Typography variant="body2">
                      Innovation & Excellence
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box className={styles.marketLeadershipBox}>
                <Typography variant="h4" className={styles.marketTitle}>
                  🏆 Market Leadership
                </Typography>
                <Typography variant="body1" className={styles.sectionText}>
                  We aim to remain at the forefront of the global technology
                  market with our products and services. We will continue to
                  develop partnerships through our distributors and customers
                  (financial, government, or commercial).
                </Typography>
                <Box className={styles.partnersBox}>
                  <Chip label="Financial" className={styles.partnerChip} />
                  <Chip label="Government" className={styles.partnerChip} />
                  <Chip label="Commercial" className={styles.partnerChip} />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Work Ethics & Core Values */}
      <Box className={styles.valuesSection}>
        <Container maxWidth="lg">
          <Box className={styles.sectionHeader}>
            <Typography
              variant="h2"
              className={styles.sectionTitle}
              sx={{
                fontSize: { xs: "2rem", sm: "2.5rem", md: "2.8rem" },
              }}
            >
              Work Ethics &{" "}
              <span className={styles.highlight}>Core Values</span>
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {/* Work Ethics */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Box className={styles.ethicsBox}>
                <Typography variant="h5" className={styles.ethicsTitle}>
                  ⚡ Work Ethics
                </Typography>
                <Box className={styles.ethicsGrid}>
                  {workEthics.map((ethic, index) => (
                    <Box key={index} className={styles.ethicItem}>
                      <Box className={styles.ethicNumber}>{index + 1}</Box>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {ethic.title}
                        </Typography>
                        <Typography variant="caption" color="#3f5a79">
                          {ethic.description}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>

            {/* Core Values */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Box className={styles.valuesBox}>
                <Typography variant="h5" className={styles.valuesTitle}>
                  🌟 Core Values
                </Typography>
                <Box className={styles.valuesGrid}>
                  {coreValues.map((value, index) => (
                    <Box key={index} className={styles.valueItem}>
                      <Box
                        className={styles.valueIcon}
                        sx={{
                          background: `${value.color}20`,
                          color: value.color,
                        }}
                      >
                        {value.icon}
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {value.title}
                        </Typography>
                        <Typography variant="caption" color="#3f5a79">
                          Guiding principle
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Partners Section */}
      <Box className={styles.partnersSection}>
        <Container maxWidth="lg">
          <Box className={styles.partnersContent}>
            <Typography variant="h5" className={styles.partnersTitle}>
              🤝 Wavelength/Wavepalm Partners
            </Typography>
            <Typography variant="body1" className={styles.partnersText}>
              Wavelength/Wavepalm partners with various companies across a range
              of sectors to provide cutting edge solutions and integrated
              services. We work closely with technology leaders to ensure that
              our clients benefit from dependable and innovative delivery across
              all solutions.
            </Typography>
            <Typography variant="body1" className={styles.partnersText}>
              We have a work ethics based on{" "}
              <strong>integrity and excellent service</strong> which helps us to
              retain our clients base and earn us new businesses every day.
            </Typography>
            <Box className={styles.taglineBox}>
              <Typography variant="h6" className={styles.tagline}>
                We are always ahead.
              </Typography>
              <Typography variant="h6" className={styles.taglineSub}>
                Providing Professional Solutions for Your Business.
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default About;
